import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { Files } from "~/services/Files"
import { PrinticularApi } from "~/services/PrinticularApi"

export class VNZService {
  private authorizationUrl: string
  private baseUrl: string
  private imageProxyUrl: string
  private printicularApi: PrinticularApi

  constructor(
    baseUrl: string,
    authorizationUrl: string,
    imageProxyUrl: string,
    printicularApi: PrinticularApi
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "")
    this.authorizationUrl = authorizationUrl.replace(/\/+$/, "")
    this.imageProxyUrl = imageProxyUrl.replace(/\/+$/, "")
    this.printicularApi = printicularApi
  }

  public getAuthenticationUrl(clientId: number, deviceToken: string) {
    const state = encodeURIComponent(
      JSON.stringify({
        deviceToken,
        clientId,
      })
    )
    const url = `${this.authorizationUrl}&state=${state}`

    return url
  }

  public async getAccessToken(authorizationCode: string) {
    return this.printicularApi.getVerizonAccessToken(authorizationCode)
  }

  public async fullview(accessToken: string) {
    // const result = await fetch(`${this.baseUrl}/vzcloud-apis/v1/fullview`, {
    //   method: "GET",
    //   headers: {
    //     Authorization: `Bearer ${accessToken}`,
    //   },
    // })

    // use our image proxy to avoid CORS issues when fetching full view from VNZ API
    const result = await fetch(
      `${this.imageProxyUrl}/vzcloud-apis/v1/fullview`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    const json = await result.json()
    const data: {
      count: number
      folder: string[]
      file: VNZImageEntity[]
    } = json.data

    return data
  }

  public async fetchAllImages(accessToken: string) {
    const allFilesResults = await this.fullview(accessToken)
    const allImages = allFilesResults.file.reduce<VNZImageEntity[]>(
      (acc, file) => {
        if (!file.systemAttributes?.MimeType?.startsWith("image/")) {
          return acc
        }

        // inconsitent API response, some files have a trailing slash in the parentPath, some don't
        const parentPath = `${file.parentPath.replace(/^\/+|\/+$/g, "")}`

        acc.push({
          ...file,
          id: file.checksum,
          isLoadingThumbnail: false,
          hasLoadedThumbnail: false,
          thumbnailUrl: `${this.baseUrl}/vzcloud-apis/v1/thumbnails/${file.contentToken}?size=l`,
          isLoadingFullImage: false,
          hasLoadedFullImage: false,
          fullImageUrl: `${this.imageProxyUrl}/vzcloud-apis/v1/files/${parentPath}/${file.name}`,
        })

        return acc
      },
      []
    )

    return allImages
  }

  public async fetchThumbnail(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.thumbnailUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!imageResult.ok) {
      throw new Error(
        `Failed to fetch thumbnail. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: imageResult.headers.get("content-type") || "image/*",
    })
    const url = URL.createObjectURL(blob)

    return url
  }

  public async fetchFullImage(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.fullImageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (
      imageResult.status.toString().startsWith("4") ||
      imageResult.status.toString().startsWith("5")
    ) {
      throw new Error(
        `Failed to fetch full image. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    const contentType = image.systemAttributes?.MimeType || "image/jpeg"

    // create a file from the array buffer so we can convert it if necessary
    const file = new File([arrayBuffer], image.name, { type: contentType })

    // if HEIC, convert to JPEG (no-op for other formats)
    const convertedFile =
      file.type === "image/heic" ? await Files.convertHeicToJpeg(file) : file

    const url = URL.createObjectURL(convertedFile)

    return url
  }
}
